import { DestroyRef, effect, inject, signal, type Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import type { SelectItem } from '@sb/ui/select';

import { NewHiresService } from './new-hires.service';

/** Variable keys captured only for German hires — single source of truth for the country-gating. */
export const GERMAN_TRIGGER_VARIABLES = ['employment_category', 'food_handling'] as const;

export type GermanTriggerControls = ReturnType<typeof germanTriggerControls>;

/** String controls so the backend receives 'true'/'false' and the category slug, never a JSON boolean. */
export const germanTriggerControls = () => ({
  employment_category: new FormControl(
    { value: '', disabled: true },
    { nonNullable: true, validators: [Validators.required] },
  ),
  food_handling: new FormControl(
    { value: '', disabled: true },
    { nonNullable: true, validators: [Validators.required] },
  ),
});

/**
 * Enabling and disabling with the country beats toggling validators at runtime. A disabled control is both valid
 * and absent from form.value, so a non-German hire stays submittable and never submits the triggers.
 */
export const useGermanTriggerControlsSync = (controls: GermanTriggerControls, isGermanHire: Signal<boolean>) => {
  effect(() => {
    const isGerman = isGermanHire();

    for (const variable of GERMAN_TRIGGER_VARIABLES) {
      const control = controls[variable];

      if (isGerman) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    }
  });
};

/**
 * The options are backend-owned, so they are fetched rather than hardcoded. Skipping the read when the
 * German release is off keeps the request off every Dutch account.
 * Call in an injection context (field initializer or constructor).
 */
export const useEmploymentCategoryOptions = (germanReleaseEnabled: boolean): Signal<SelectItem[]> => {
  const options = signal<SelectItem[]>([]);

  if (!germanReleaseEnabled) {
    return options;
  }

  inject(NewHiresService)
    .getEmploymentCategories()
    .pipe(takeUntilDestroyed(inject(DestroyRef)))
    .subscribe((categories) => options.set(categories));

  return options;
};
